import React, { useState } from "react";
import "./GreenMachineAdmin.css";
import Button from "../../../../Button";
import LoadingOverlay from "react-loading-overlay";
import { gql, useMutation } from "@apollo/client";
import GreenMachineStats from "./GreenMachineStats";

const UPLOAD_CSV = gql`
  mutation GreenMachineAdminUploadCSVMutation($csv: Upload!) {
    uploadPercentageProjections(csvFile: $csv) {
      success
      updatedCount
      skippedRows
    }
  }
`;

const UPLOAD_CONTEST = gql`
  mutation GreenMachineAdminUploadCSVMutation($zip: Upload!) {
    uploadContestZip(zipFile: $zip) {
      success
    }
  }
`;

const UPLOAD_MANUAL_SLATE = gql`
  mutation GreenMachineAdminUploadManualSlateCSVMutation(
    $csv: Upload!
    $slateId: Int!
  ) {
    uploadManualSlate(slateId: $slateId, csvFile: $csv) {
      success
    }
  }
`;

const MARK_MISSED_CUTS = gql`
  mutation GreenMachineAdminMarkMissedCutsMutation($cutLine: Int!) {
    markMissedCuts(input: { cutLine: $cutLine }) {
      success
    }
  }
`;

export default function GreenMachineAdmin() {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(undefined);
  const [contestFiles, setContestFiles] = useState(undefined);

  const [slateFile, setSlateFile] = useState(undefined);
  const [manualSlateId, setManualSlateId] = useState(undefined);

  const [cutLine, setCutLine] = useState(undefined);

  const [uploadCSV] = useMutation(UPLOAD_CSV);
  const [uploadContest] = useMutation(UPLOAD_CONTEST);
  const [markMissedCuts] = useMutation(MARK_MISSED_CUTS);
  const [uploadManualSlateCSV] = useMutation(UPLOAD_MANUAL_SLATE);

  function onFileUpload(e) {
    e.preventDefault();

    setFile(e.target.files[0]);
  }

  function onContestUpload(e) {
    e.preventDefault();

    const failingFileNames = [];
    const filesToUpload = [];

    Array.from(e.target.files).forEach((file) => {
      // Ensures proper naming scheme
      if (/^(P_.*_)?contest-standings-\d+\.zip$/.test(file.name)) {
        filesToUpload.push(file);
      } else {
        failingFileNames.push(file.name);
      }
    });

    if (failingFileNames.length > 0) {
      alert(
        `The following files are invalid: ${failingFileNames.join(
          ", "
        )}. File names must be in the format "contest-standings-CONTESTIDNUMBER.zip"`
      );
    }

    setContestFiles(filesToUpload);
  }

  function onSlateFileUpload(e) {
    e.preventDefault();

    setSlateFile(e.target.files[0]);
  }

  function onContestSubmit(e) {
    e.preventDefault();

    console.log(contestFiles);

    if (contestFiles.length === 0) {
      return;
    }

    setLoading(true);
    contestFiles.forEach((file, index) => {
      uploadContest({ variables: { zip: file } })
        .then(() => {
          console.log("Finished", index);
          if (index === contestFiles.length - 1) {
            setLoading(false);
            e.target.files = null;
          }
        })
        .catch(() => {
          if (index === contestFiles.length - 1) {
            setLoading(false);
            alert(`Something went wrong uploading ${file.name}.`);
          } else {
            alert(
              `Something went wrong uploading ${file.name}. Trying the next one...`
            );
          }
        });
    });
  }

  function onSubmit(e) {
    e.preventDefault();

    if (!file) {
      return;
    }

    setLoading(true);

    uploadCSV({ variables: { csv: file } })
      .then(({ data }) => {
        const { updatedCount, skippedRows } =
          data.uploadPercentageProjections;
        setLoading(false);
        alert(
          `Updated ownership projections for ${updatedCount} players.${
            skippedRows ? ` Skipped ${skippedRows} row(s) with no ownership value.` : ""
          }`
        );
      })
      .catch((error) => {
        setLoading(false);
        alert(error.message || "Something went wrong.");
      });
  }

  function onSlateSubmit(e) {
    e.preventDefault();

    if (!slateFile || !manualSlateId) {
      return;
    }

    setLoading(true);

    uploadManualSlateCSV({
      variables: { csv: slateFile, slateId: Number.parseInt(manualSlateId) },
    })
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false);
        alert("Something went wrong.");
      });
  }

  function exportGMStats(e) {
    e.preventDefault();
  }

  return (
    <LoadingOverlay spinner active={loading} text={"Uploading..."}>
      <div className="gm-admin-container">
        <h1>Upload Percentages</h1>
        <label htmlFor="csv-file">CSV: </label>
        <input id="csv-file" type="file" onChange={onFileUpload} />
        <br />
        <br />
        <Button form={true} onClick={onSubmit} style={{ margin: "inherit" }}>
          Submit
        </Button>

        <br />
        <br />

        <h1>Upload Contest Zips</h1>
        <label htmlFor="contest-files">Contest Zips: </label>
        <input
          id="contest-files"
          type="file"
          onChange={onContestUpload}
          multiple
        />
        <br />
        <br />
        <Button
          form={true}
          onClick={onContestSubmit}
          style={{ margin: "inherit" }}
        >
          Submit
        </Button>

        <React.Suspense fallback="Loading stats export...">
          <GreenMachineStats />
        </React.Suspense>

        <h1>Mark Missed Cuts</h1>
        <input
          type="number"
          onChange={(e) => setCutLine(e.target.value)}
          value={cutLine}
        />
        <br />
        <br />
        <Button
          form={true}
          onClick={() => {
            if (!cutLine) {
              return;
            }

            const conf = window.confirm(`Submit cut line of: ${cutLine}?`);

            if (conf) {
              setLoading(true);

              markMissedCuts({
                variables: { cutLine: Number.parseInt(cutLine) },
                onCompleted: () => {
                  setLoading(false);
                },
                onError: () => {
                  alert("Something went wrong.");
                  setLoading(false);
                },
              });
            }
          }}
          style={{ margin: "inherit" }}
        >
          Submit
        </Button>

        <h1>Upload Manual Slate</h1>
        <label htmlFor="csv-file">CSV: </label>
        <input id="csv-file" type="file" onChange={onSlateFileUpload} />
        <label>
          Slate ID:{" "}
          <input
            type="number"
            onChange={(e) => {
              e.preventDefault();
              setManualSlateId(e.target.value);
            }}
          />{" "}
        </label>
        <br />
        <br />
        <Button
          form={true}
          onClick={onSlateSubmit}
          style={{ margin: "inherit" }}
        >
          Submit
        </Button>
      </div>
    </LoadingOverlay>
  );
}
